import { OffScreen } from "@/js/OffScreen.js";
import { FirebaseCommon } from "@/js/FirebaseCommon.js";
import { GetCanvasSize, GetSampleImageURL, PegeViewData } from "@/js/Const.js";
import { FontData } from "@/js/FontData.js";
import { AcryShippingFeeBase, AcryShippingFee, GetAcryProductPrice } from "@/js/Const.js";
import QRious from './qrious.js';
import Encoding from './encoding.js';
import { Analytics } from "@/js/Analytics.js";
import { Logger } from '@/js/utils/Logger.js';
import { Common } from '@/js/common.js'

// デザインを編集するための準備を行う
// デザイン画像のダウンロードとそれらをvuexへ登録する
const EditDesignPreparation = async function(designId, productId, isShareCodeDraft, store){
  const results = [];

  // デザイン画像
  results.push(PreparationDesign(designId, store));

  // カットライン
  if(isShareCodeDraft){
    results.push(PreparationPngCutline(designId, productId, store));
  }else{
    results.push(PreparationSvgCutline(designId, productId, store));
  }

  await Promise.all(results);
}

const PreparationDesign = async function(designId, store){
  const designImageUrl = await FirebaseCommon.getImageUrl(FirebaseCommon.getDesignFileName(designId));
  const designFile = await FirebaseCommon.downloadPngImage(designImageUrl);
  store.commit('setDesignImageFile', designFile);
  store.commit('setDesignImageURL', URL.createObjectURL(designFile));
}

const PreparationPngCutline = async function(designId, productId, store){
  // pngカットライン
  var cutlineImageUrl = await FirebaseCommon.getImageUrl(FirebaseCommon.getPngCutlineFileName(designId));
  var cutlineFile = await FirebaseCommon.downloadPngImage(cutlineImageUrl);
  store.commit('setScCutlineImageFile', cutlineFile);
  store.commit('setScCutlineImageURL', URL.createObjectURL(cutlineFile));
  // 画像の輪郭を抽出
  let canvasSize = GetCanvasSize(productId);
  const imageWidth = canvasSize.width;
  const imageHeight = canvasSize.height;
  let blob = await CreateFrameCutlineImage(cutlineImageUrl, imageWidth, imageHeight);
  store.commit('setFrameCutlineImageFile', blob);
  store.commit('setFrameCutlineImageURL', URL.createObjectURL(blob));
}

const PreparationSvgCutline = async function(designId, productId, store){
  // svgカットライン
  var cutlineImageUrl = await FirebaseCommon.getImageUrl(FirebaseCommon.getSvgCutlineFileName(designId));
  var cutlineFile = await FirebaseCommon.downloadSvgImage(cutlineImageUrl);
  store.commit('setCutlineImageFile', cutlineFile);
  store.commit('setCutlineImageURL', URL.createObjectURL(cutlineFile));

  let canvasSize = GetCanvasSize(productId);
  const imageWidth = canvasSize.width;
  const imageHeight = canvasSize.height;
  let offScreen = new OffScreen(imageWidth, imageHeight);
  let image = await CreateImageElement(cutlineImageUrl);
  offScreen.drawImage(image, 0, 0, imageWidth, imageHeight);
  const blob = await new Promise( (resolve)=>{ offScreen.toBlob(function(blob){ resolve(blob); }) });
  store.commit('setFrameCutlineImageFile', blob);
  store.commit('setFrameCutlineImageURL', URL.createObjectURL(blob));
}

const DiscountRatio = function( quantity ){
  if( quantity < 10 ){
    return 0; // 割引なし
  }else if( quantity < 20 ){
    return 10; // 10%OFF
  }else if( quantity < 30 ){
    return 20; // 20%OFF
  }else if( quantity < 40 ){
    return 30; // 30%OFF
  }else if( quantity < 50 ){
    return 40; // 40%OFF
  }else if( quantity < 100 ){
    return 50; // 50%OFF
  }else {
    return 60; // 60%OFF
  }
}

// 送料は注文合計金額で変更
const DelivPrice = function( totalPrice ){
  if( totalPrice < 10000 ){
    return 500;
  }else if( totalPrice < 30000 ){
    return 1000;
  }else{
    return 0;
  }
}

// blobデータの画像データをリサイズを行う
const ResizeImageBlob = async function(blob, width, height){
  const image = await CreateImageElement(URL.createObjectURL(blob));
  let offScreen = new OffScreen(width, height);
  offScreen.drawImage( image, 0, 0, width, height );
  const resizeBlob = await new Promise( (resolve)=>{
    offScreen.toBlob(function(resizeBlob){
      resolve(resizeBlob);
    })
  } );
  return resizeBlob;
}

// ImageElementを作成して指定されたURLの画像をロードした状態で返却する
const CreateImageElement = async function( imageUrl ){
  return new Promise((resolve, reject) => {
    var imageElement = new Image();
    imageElement.crossOrigin = 'Anonymous'
    imageElement.onload = () => {
      resolve(imageElement);
    };
    imageElement.onerror = (e) => {
      reject(e);
    };
    imageElement.src = imageUrl;
  });
}

var cv;

// シェアコード入稿画像から輪郭を抽出して、線画にしてそのblobを返す
// 塗状のSVGから線状のSVGへの変換にも使用
const CreateFrameCutlineImage = async function( imageUrl ){
  Logger.info("Start OpenCV");

  if( !cv ){
    cv = require('@/js/opencv.js');
    await new Promise((resolve)=>
    {
      cv['onRuntimeInitialized'] = () => {
        resolve();
        Logger.info("Initialized OpenCV");
      }
    });
  }

  // シェアコード入稿のカットライン
  let element = await CreateImageElement(imageUrl);
  const imageWidth = element.width;
  const imageHeight = element.height;

  let cutlineOffScreen = new OffScreen(imageWidth, imageHeight);
  cutlineOffScreen.drawWhite();
  cutlineOffScreen.drawImage(element, 0, 0, imageWidth, imageHeight);

  let src = cv.imread(cutlineOffScreen.canvas);
  let dst = cv.Mat.zeros(src.rows, src.cols, cv.CV_8UC3);
  cv.cvtColor(src, src, cv.COLOR_RGBA2GRAY, 0);
  cv.threshold(src, src, 177, 200, cv.THRESH_BINARY);

  let contours = new cv.MatVector();
  let hierarchy = new cv.Mat();
  cv.findContours(src, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

  let contoursColor = new cv.Scalar(255, 255, 255);

  // 描画していく
  for (let i = 0; i < contours.size(); ++i) {

    // 一番外枠は描画しない
    let cnt = contours.get(i);
    let rect = cv.boundingRect(cnt);
    cnt.delete();

    if( rect.width == imageWidth && rect.height == imageHeight ){
      continue;
    }
    let lineSize = imageWidth/300;
    cv.drawContours(dst, contours, i, contoursColor, lineSize, 8, hierarchy, 100);
  }

  // canvasに結果を出力
  // let canvas = document.createElement("canvas");
  let offScreen = new OffScreen(imageWidth, imageHeight);
  cv.imshow(offScreen.canvas, dst);

  // OpenCV関連を削除
  src.delete(); dst.delete(); contours.delete(); hierarchy.delete();

  // 背景色を透過
  offScreen.transparent(0, 0, 0);

  const blob = await new Promise( (resolve)=>{
    offScreen.toBlob(function(blob){
      resolve(blob);
    })
  } );

  return blob;
}

const CreateSampleDesignImage = async function( imageUrl, width, height, productId ){

  const imageWidth = width;
  const imageHeight = height;

  let element = await CreateImageElement(imageUrl);
  let designOffScreen = new OffScreen(imageWidth, imageHeight);
  designOffScreen.drawImage(element, 0, 0, imageWidth, imageHeight);

  let sampleImageUrl = GetSampleImageURL(productId);
  let sampleElement = await CreateImageElement(sampleImageUrl);
  designOffScreen.drawImage(sampleElement, 0, 0, imageWidth, imageHeight);

  const blob = await new Promise( (resolve)=>{
    designOffScreen.toBlob(function(blob){
      resolve(blob);
    })
  } );

  return blob;
}

var loadedFontList = [];

// テキストを画像化し、画像情報をまとめたオブジェクトを変換
// テキストは一行のみを想定
const CreateTextImage = async function(text, font, color, size = 100){

  // 未使用のフォントの場合はダウンロード処理
  if( !loadedFontList.includes(font) ){
    // 使用するフォントがダウンロードされるまで待機
    const baseUrl = process.env.BASE_URL;
    const fontData = new FontData();
    const useFont = fontData.getFontList().find(el => el.familyName == font) ?? fontData.getFontList().find(el => el.familyName == "AnitoStd-M");
    const fontFace = new FontFace( useFont.familyName, `url(${baseUrl+useFont.url})` );
    await fontFace.load();
    // 使用できるように登録
    document.fonts.add(fontFace);
    loadedFontList.push(font);
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  //const size = 100;

  // フォント情報を定義
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = size + "px " + font;

  // 空文字はエラーになる
  if( text == "" ) text = "　";

  // テキストのサイズを計算
  const mesure = context.measureText(text);
  const textWidth =  mesure.width;
  const textHeight =  mesure.actualBoundingBoxAscent + mesure.actualBoundingBoxDescent + (size/2);

  // キャンバスに情報を設定し画像を生成
  canvas.width = textWidth;
  canvas.height = textHeight;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = size + "px " + font;
  context.fillStyle = color;
  context.fillText(text, textWidth/2, textHeight/2);
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  return {blob:blob, width:textWidth, height:textHeight};
}

const RotateImage = async function(imageElement, rotate, align="center"){
  let size = parseInt(Math.max(imageElement.width, imageElement.height) * 2.5);
  let offScreen = new OffScreen(size, size);
  switch(align){
    case "left":
      offScreen.drawImageRotate(imageElement, (size/2), (size/2)-(imageElement.height/2), imageElement.width, imageElement.height, rotate);
      break;
    case "center":
      offScreen.drawImageRotate(imageElement, (size/2)-(imageElement.width/2), (size/2)-(imageElement.height/2), imageElement.width, imageElement.height, rotate);
      break;
    case "right":
      offScreen.drawImageRotate(imageElement, (size/2)-(imageElement.width), (size/2)-(imageElement.height/2), imageElement.width, imageElement.height, rotate);
      break;
  }
  const blob = await new Promise(resolve => offScreen.toBlob(resolve));
  return {blob:blob, width:size, height:size};
}

const CreateQRImage = async function(text, color, bgColor){
  if( text == "" )
  {
    const canvas = document.createElement("canvas");
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    return {blob:blob};
  }

  const unicodeArray = Encoding.stringToCode(text);
  const sjisArray = Encoding.convert(unicodeArray, {
    to: 'SJIS',
    from: 'UNICODE'
  });
  var qr = new QRious({
    value:Encoding.codeToString(sjisArray),
    foreground:color,
    background:bgColor,
    size:100,
  });

  var base64 = qr.toDataURL()
  // Base64からバイナリへ変換
  var bin = atob(base64.replace(/^.*,/, ''));
  var buffer = new Uint8Array(bin.length);
  for (var i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
  }
  // Blobを作成
  const blob = new Blob([buffer.buffer], {
      type: 'image/png'
  });
  return {blob:blob};
}

/**
 * SVGのFileデータにフレームを追加
 * @param {FILE} svgFile アップロードされた画像ファイル
 * @param {String} productId 商品ID（商品のサイズに合わせるため）
 * @return {FILE} フレーム追加後
 */
const CombineSVGFrame = async function(svgFile, productId){
  const reader = new FileReader();
  const svgData = await new Promise((resolve)=>{
    // ファイルの読み込みが終了したら発火するイベント
    reader.onload = function(e) {
      resolve(e.target.result)
    };
    // SVGとしてファイルを読み込む
    reader.readAsText(svgFile);
  });

  // 編集するためにDOMに変換
  const parser = new DOMParser();
  const svgDom = parser.parseFromString(svgData, "image/svg+xml").documentElement;

  // サイズに関する情報を削除
  // svgDom.removeAttribute("width");
  // svgDom.removeAttribute("height");
  // svgDom.removeAttribute("viewBox");
  // console.log(svgDom);

  // フレーム用のパスを生成
  const canvasSize = GetCanvasSize(productId);
  const framePath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  framePath.setAttribute("d", "M0,0 L0,"+canvasSize.height+" L"+canvasSize.width+","+canvasSize.height+" L"+canvasSize.width+",0 Z");
  framePath.setAttribute("fill", "none");
  // framePath.setAttribute("stroke", "black");

  // 既存のSVGにフレームを合成
  svgDom.appendChild(framePath);

  const serializer = new XMLSerializer();                             // シリアライザーを生成
  const svgString = serializer.serializeToString(svgDom);             // DOMデータを文字列に変換
  const svgBlob = new Blob([svgString], {type: 'image/svg+xml'});     // 文字列データからBlobに変換
  return new File([svgBlob], svgFile.name, {type: 'image/svg+xml'});  // BlobからFileを生成しなおす
}

/**
 * SVGから表示用のPNG画像を生成
 * @param {String} fileUrl 画像のURL
 * @param {String} productId 商品ID（商品のサイズに合わせるため）
 * @return {String} PNGに変換後の画像URL
 */
const ConvertSvgToPng = async function( fileUrl, productId ){
  let canvasSize = GetCanvasSize(productId);
  const imageWidth = canvasSize.width;
  const imageHeight = canvasSize.height;
  let offScreen = new OffScreen(imageWidth, imageHeight);

  let image = await CreateImageElement(fileUrl);
  offScreen.drawImage(image, 0, 0, imageWidth, imageHeight);
  offScreen.replacement("#000000"); // 後ほど色を変更するので一旦何色でも良い

  const blob = await new Promise((resolve) => {
    offScreen.toBlob(function (blob) {
      resolve(blob);
    });
  });

  return URL.createObjectURL(blob);
}

/**
 * PNG画像の色を指定の色に塗りつぶす
 * @param {String} fileUrl 画像のURL
 * @param {String} color 塗りつぶすカラーコード
 * @return {String} 塗りつぶした後の画像URL
 */
const ReplacePngColor = async function( fileUrl, color ){
  const image = await CreateImageElement(fileUrl);
  const imageWidth = image.width;
  const imageHeight = image.height;
  let offScreen = new OffScreen(imageWidth, imageHeight);
  offScreen.drawImage(image, 0, 0, imageWidth, imageHeight);
  offScreen.replacement(color);

  const blob = await new Promise((resolve) => {
    offScreen.toBlob(function (blob) {
      resolve(blob);
    });
  });

  return URL.createObjectURL(blob);
}


const ClearSVGStyle = async function(svgFile){
  console.log("CombineSVGFrame");
  console.log(svgFile);

  const reader = new FileReader();
  const svgData = await new Promise((resolve)=>{
    // ファイルの読み込みが終了したら発火するイベント
    reader.onload = function(e) {
      resolve(e.target.result)
    };
    // SVGとしてファイルを読み込む
    reader.readAsText(svgFile);
  });

  // 編集するためにDOMに変換a
  const parser = new DOMParser();
  const svgDom = parser.parseFromString(svgData, "image/svg+xml").documentElement;

  // サイズに関する情報を削除
  // svgDom.removeAttribute("width");
  // svgDom.removeAttribute("height");
  // svgDom.removeAttribute("viewBox");
  // console.log(svgDom);
  // console.log(typeof svgDom);
  // console.log(parser.parseFromString(svgData, "image/svg+xml"));
  const elements = svgDom.getElementsByTagName('style');
  if( elements.length ){
    elements.item(0).remove();
  }
  // console.log(svgDom);

  // フレーム用のパスを生成
  // const canvasSize = GetCanvasSize();
  // const framePath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  // framePath.setAttribute("d", "M0,0 L0,"+canvasSize.height+" L"+canvasSize.width+","+canvasSize.height+" L"+canvasSize.width+",0 Z");
  // framePath.setAttribute("fill", "none");
  // framePath.setAttribute("stroke", "black");

  // 既存のSVGにフレームを合成
  // svgDom.appendChild(framePath);

  const serializer = new XMLSerializer();                             // シリアライザーを生成
  const svgString = serializer.serializeToString(svgDom);             // DOMデータを文字列に変換
  const svgBlob = new Blob([svgString], {type: 'image/svg+xml'});     // 文字列データからBlobに変換
  return new File([svgBlob], svgFile.name, {type: 'image/svg+xml'});  // BlobからFileを生成しなおす
}

// const CreateImageElement = async function( designUrl, maskUrl ){
//   return new Promise((resolve) => {
//     var imageElement = new Image();
//     imageElement.crossOrigin = 'Anonymous'
//     imageElement.onload = () => {
//       resolve(imageElement);
//     };
//     imageElement.src = imageUrl;
//   });
// }

const GenerateUUID = function()
{
  const _lut = ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '0a', '0b', '0c', '0d', '0e', '0f', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '1a', '1b', '1c', '1d', '1e', '1f', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '2a', '2b', '2c', '2d', '2e', '2f', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '3a', '3b', '3c', '3d', '3e', '3f', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '4a', '4b', '4c', '4d', '4e', '4f', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '5a', '5b', '5c', '5d', '5e', '5f', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '6a', '6b', '6c', '6d', '6e', '6f', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '7a', '7b', '7c', '7d', '7e', '7f', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '8a', '8b', '8c', '8d', '8e', '8f', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '9a', '9b', '9c', '9d', '9e', '9f', 'a0', 'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'a9', 'aa', 'ab', 'ac', 'ad', 'ae', 'af', 'b0', 'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8', 'b9', 'ba', 'bb', 'bc', 'bd', 'be', 'bf', 'c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'ca', 'cb', 'cc', 'cd', 'ce', 'cf', 'd0', 'd1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9', 'da', 'db', 'dc', 'dd', 'de', 'df', 'e0', 'e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9', 'ea', 'eb', 'ec', 'ed', 'ee', 'ef', 'f0', 'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'fa', 'fb', 'fc', 'fd', 'fe', 'ff'];
  const d0 = Math.random() * 0xffffffff | 0;
  const d1 = Math.random() * 0xffffffff | 0;
  const d2 = Math.random() * 0xffffffff | 0;
  const d3 = Math.random() * 0xffffffff | 0;
  const uuid = _lut[d0 & 0xff] + _lut[d0 >> 8 & 0xff] + _lut[d0 >> 16 & 0xff] + _lut[d0 >> 24 & 0xff] + '-' + _lut[d1 & 0xff] + _lut[d1 >> 8 & 0xff] + '-' + _lut[d1 >> 16 & 0x0f | 0x40] + _lut[d1 >> 24 & 0xff] + '-' + _lut[d2 & 0x3f | 0x80] + _lut[d2 >> 8 & 0xff] + '-' + _lut[d2 >> 16 & 0xff] + _lut[d2 >> 24 & 0xff] + _lut[d3 & 0xff] + _lut[d3 >> 8 & 0xff] + _lut[d3 >> 16 & 0xff] + _lut[d3 >> 24 & 0xff]; // .toLowerCase() here flattens concatenated strings to save heap memory space.

  return uuid.toLowerCase();
}

/**
 * Date型が二つのDate型の間かチェック
 * @param {Date} target
 * @param {Date} start
 * @param {Date} end
* @return {boolean} 間かどうか
 */
const DateIsBetween = function(target, start, end) {
  return target >= start && target <= end;
}

const CalculationTicketOrderPrice = function( productId, isShippingFee, quantity, isSample ){
  let result = {
    ticketPrice: 0,
    discount: 0,
    delivPrice: 0,
    totalTax: 0,
    totalPrice: 0
  };

  const ProductPrice = GetAcryProductPrice(productId);
  const ProductTax = ProductPrice.price - ProductPrice.priceBase;
  result.ticketPrice = Math.floor(ProductPrice.priceBase * quantity);

  if( !isSample ){
    const Ratio = DiscountRatio(quantity);
    result.discount = Math.floor(ProductPrice.priceBase * quantity * Ratio / 100);

    // 割引を適応した後の価格に対して消費税を設定
    result.totalTax = Math.floor((result.ticketPrice - result.discount) * Common.getTax());

    if( !isShippingFee ){
      // 企業負担
      const DelivFeeTax = AcryShippingFee - AcryShippingFeeBase;
      result.delivPrice = Math.floor(AcryShippingFeeBase * quantity);
      result.totalTax += Math.floor(DelivFeeTax * quantity);
    }
  }else{
    // サンプル注文
    result.totalTax = Math.floor(ProductTax * quantity);
  }

  result.totalPrice = result.totalTax + result.delivPrice + result.ticketPrice - result.discount;

  return result;
}

/**
 * 送られてきた画面IDに紐づくURLを設定する
 * @param {Number} centerViewId URLを設定したいViewID
 */
const SetCenterViewUrl = function( centerViewId ){
  const ViewData = PegeViewData.find( e => {return e.viewId == centerViewId } );
  if( ViewData ){
    const url = new URL(ViewData.path, window.location.origin)
    history.replaceState(null, '', url)
    document.title = ViewData.title;
    Analytics.pageView();
  }else{
    Logger.error("存在しない画面ID" + centerViewId);
  }
}

/**
 * アップロードされた画像ファイルをトリミングし返す
 * @param {FILE} file アップロードされた画像ファイル
 * @param {String} productId 商品ID（商品のサイズに合わせてトリミング）
 * @return {FILE} トリミング後の画像ファイル
 */
const TrimmingDesignImageFile = async function( file, productId ){
  let canvasSize = GetCanvasSize(productId);
  let offScreen = new OffScreen(canvasSize.width, canvasSize.height);
  let image = await CreateImageElement(URL.createObjectURL(file));
  offScreen.drawImage(image, 0, 0, image.width, image.height);
  let blob = await new Promise((resolve) => {
    offScreen.toBlob(function (blob) {
      resolve(blob);
    });
  });

  return new File([blob], file.name, { type: "image/png" });
}

export { SetCenterViewUrl, CalculationTicketOrderPrice, DateIsBetween, CreateImageElement, CreateFrameCutlineImage, CreateSampleDesignImage, EditDesignPreparation, DiscountRatio, DelivPrice, CreateTextImage, RotateImage, CreateQRImage, ResizeImageBlob, CombineSVGFrame, ClearSVGStyle, GenerateUUID, TrimmingDesignImageFile, ConvertSvgToPng, ReplacePngColor }